/**
 * Audit log API may store `label` as a string, but some fields (e.g. imageCapture rows)
 * persist structured objects. React cannot render those as children — normalize here.
 */
export function formatAuditLogDisplayValue(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return "";
    return value.map((v) => formatAuditLogDisplayValue(v)).filter(Boolean).join("; ");
  }
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    const looksLikePhotoRow =
      "fileId" in o ||
      "fileName" in o ||
      "backupFileName" in o ||
      (typeof o.title === "string" && o.title.trim() !== "") ||
      (typeof o.description === "string" && o.description.trim() !== "");
    if (looksLikePhotoRow) {
      const title = o.title != null ? String(o.title).trim() : "";
      const desc = o.description != null ? String(o.description).trim() : "";
      const fileName =
        o.fileName != null
          ? String(o.fileName).trim()
          : o.backupFileName != null
            ? String(o.backupFileName).trim()
            : "";
      const parts = [title, desc, fileName].filter(Boolean);
      return parts.length > 0 ? parts.join(" — ") : "[Photo]";
    }
    try {
      return JSON.stringify(value);
    } catch {
      return "[Complex value]";
    }
  }
  return String(value);
}

/** True when this side of a diff should show the “filled” highlight (non-empty content). */
export function auditFieldHasVisualContent(field?: { value?: unknown; label?: unknown }): boolean {
  if (!field) return false;
  if (formatAuditLogDisplayValue(field.label) !== "") return true;
  const v = field.value;
  if (v === undefined || v === null) return false;
  if (typeof v === "string") return v.trim() !== "";
  if (typeof v === "number" || typeof v === "boolean") return true;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === "object") return Object.keys(v as object).length > 0;
  return false;
}
